/* istanbul ignore file */
import React, { useEffect, useRef } from 'react';
import { cx as classNames } from '@linaria/core';
import { isNil } from 'lodash';
import { isBboxShapeProps } from 'utils/typeguards/predictionOutputs';
import { thumbWrapper } from 'components/Canvas/RegionThumb/RegionThumb.styles';
import { RegionThumbProps, THUMB_SIZE, calculateAspectRatioFit } from 'components/Canvas/RegionThumb/common';

export const RectRegionThumb: React.FC<React.PropsWithChildren<RegionThumbProps>> = ({
  regionData,
  children,
  className,
  imageData,
  id,
  frameVideo,
  size,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement & { width: number; height: number }>(null);

  useEffect(() => {
    if (regionData && isBboxShapeProps(regionData) && imageData && ('image' in imageData || imageData.video)) {
      const { bbox } = regionData;
      const rightCol = bbox.right_col || 1;
      const leftCol = bbox.left_col || 0;
      const bottomRow = bbox.bottom_row || 1;
      const topRow = bbox.top_row || 0;

      const bboxW = rightCol - leftCol;
      const bboxH = bottomRow - topRow;
      const { width: naturalWidth, height: naturalHeight } = imageData;

      const cropW = bboxW * naturalWidth;
      const cropH = bboxH * naturalHeight;
      const sx = leftCol * naturalWidth;
      const sy = topRow * naturalHeight;

      const calc = calculateAspectRatioFit(cropW, cropH, THUMB_SIZE, THUMB_SIZE);
      const container = containerRef.current;
      const canvas = canvasRef.current as HTMLCanvasElement;
      if (container && canvas) {
        container.width = calc.width;
        container.height = calc.height;

        canvas.width = calc.width;
        canvas.height = calc.height;
        const context = canvas.getContext('2d') as CanvasRenderingContext2D;

        if ('image' in imageData) {
          context.drawImage(imageData.image, sx, sy, cropW, cropH, 0, 0, calc.width, calc.height);
        } else if (imageData.video.paused && frameVideo) {
          context.drawImage(frameVideo.video, sx, sy, cropW, cropH, 0, 0, calc.width, calc.height);
        } else {
          context.drawImage(imageData.video, sx, sy, cropW, cropH, 0, 0, calc.width, calc.height);
        }
      }
    }
  }, [imageData, regionData, frameVideo?.updatedFrameTime]);

  return (
    <div ref={containerRef} className={classNames('region-thumb', thumbWrapper, className)} role="presentation">
      {children}
      <canvas
        data-testid={`region-thumb-${id}`}
        ref={canvasRef}
        {...(!isNil(size) && {
          style: {
            height: size,
            width: size,
          },
        })}
      />
    </div>
  );
};
